export default function TermsPage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Terms of Service
          </h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
            Last Updated: 12 April 2026
          </p>
        </div>
      </div>

      {/* Introduction */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <p className="text-slate-600 dark:text-slate-300">
              Welcome to CyberActions (&quot;CyberActions&quot;, &quot;we&quot;, &quot;us&quot;, or
              &quot;our&quot;). These Terms of Service (&quot;Terms&quot;) govern your access to and
              use of the CyberActions platform, website, and services (collectively, the
              &quot;Service&quot;).
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              By accessing or using the Service, you agree to be bound by these Terms.
            </p>
          </div>
        </div>
      </div>

      {/* 1. Acceptance of Terms */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              1. Acceptance of Terms
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              By creating an account, connecting a system, or using the Service, you confirm that:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>You have the legal authority to act on behalf of the organisation using the Service</li>
              <li>You agree to comply with these Terms in full</li>
              <li>If you do not agree, you must not access or use the Service</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 2. Description of the Service */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              2. Description of the Service
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              CyberActions provides a cloud-based cybersecurity posture auditing and compliance
              monitoring platform.
            </p>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              2.1 Supported Platforms
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The Service is designed exclusively to analyse security configurations within:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Microsoft 365</li>
              <li>Google Workspace</li>
            </ul>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              No other third-party platforms are supported unless explicitly stated.
            </p>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              2.2 Read-Only Functionality
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The Service operates strictly in a read-only capacity:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>It retrieves and analyses security configuration metadata</li>
              <li>It does not modify, delete, or alter system settings or user data</li>
              <li>It does not perform enforcement actions within customer environments</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              2.3 Advisory Nature
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The Service provides automated insights and recommendations only and:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Does not constitute professional cybersecurity advice</li>
              <li>
                Is not a substitute for manual audits, penetration testing, or managed security
                services
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 3. User Responsibilities and Acceptable Use */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              3. User Responsibilities and Acceptable Use
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">You agree to:</p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Provide accurate and complete account information</li>
              <li>Maintain the confidentiality of your credentials</li>
              <li>Ensure you have legal authorisation to connect and audit any system</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              Prohibited Use
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">You must not:</p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Access or audit systems without proper authorisation</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Attempt to reverse engineer or exploit the Service</li>
              <li>Use the Service for unlawful purposes</li>
              <li>Use the Service in life-critical or high-risk environments</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 4. API Access and Integrations */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              4. API Access and Integrations
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The Service connects to third-party systems via official APIs using secure OAuth
              authentication.
            </p>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              4.1 Permissions
            </h3>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>CyberActions requests read-only access only</li>
              <li>We do not request or store user passwords</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              4.2 Revocation
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              You may revoke access at any time through your Microsoft or Google administrative
              settings.
            </p>
          </div>
        </div>
      </div>

      {/* 5. Data Processing and Privacy */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              5. Data Processing and Privacy
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              CyberActions processes limited technical metadata necessary to provide the Service.
            </p>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              5.1 Data Scope
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">We may process:</p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>MFA status</li>
              <li>Device compliance information</li>
              <li>User roles and permissions</li>
              <li>Security configuration data</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              5.2 No Content Access
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We do not access or store:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Email contents</li>
              <li>Documents or files</li>
              <li>Private messages</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              5.3 Compliance
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              All personal data is handled in accordance with UK GDPR.
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Please refer to our Privacy Policy for full details.
            </p>
          </div>
        </div>
      </div>

      {/* 6. Data Retention and Deletion */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              6. Data Retention and Deletion
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We retain customer data only for as long as necessary to provide the Service.
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>
                Data is securely deleted after account termination within a reasonable timeframe
              </li>
              <li>
                Certain data may be retained where required by law or for legitimate business
                purposes
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 7. Service Availability */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              7. Service Availability
            </h2>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>The Service is provided on an &quot;as available&quot; basis</li>
              <li>We do not guarantee uninterrupted or error-free operation</li>
              <li>Maintenance, updates, or third-party outages may affect availability</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 8. Third-Party Dependencies */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              8. Third-Party Dependencies
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The Service relies on third-party providers, including Microsoft and Google.
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>
                We are not responsible for outages, API changes, or inaccuracies caused by these
                providers
              </li>
              <li>Your use of the Service is subject to your agreements with those providers</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 9. Subscription, Billing, and Trials */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              9. Subscription, Billing, and Trials
            </h2>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              9.1 Fees
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Access to the Service may require a paid subscription billed monthly or annually.
            </p>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              9.2 Free Trials
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We may offer free trial access:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Trial features may be limited</li>
              <li>Access may be restricted after the trial unless upgraded</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              9.3 Payment Terms
            </h3>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Fees are due in advance</li>
              <li>Non-payment may result in suspension or termination</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              9.4 Refunds
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Fees are non-refundable except where required by UK law.
            </p>
          </div>
        </div>
      </div>

      {/* 10. Customer Responsibilities for Security */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              10. Customer Responsibilities for Security
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              You remain fully responsible for:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Implementing security controls</li>
              <li>Acting on recommendations provided</li>
              <li>Maintaining the security of your environment</li>
            </ul>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              CyberActions does not enforce security configurations.
            </p>
          </div>
        </div>
      </div>

      {/* 11. Disclaimer of Warranties */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              11. Disclaimer of Warranties
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The Service is provided &quot;as is&quot; and &quot;as available&quot; without
              warranties of any kind.
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We disclaim all warranties, including:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Accuracy</li>
              <li>Reliability</li>
              <li>Fitness for a particular purpose</li>
              <li>Uninterrupted availability</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 12. Limitation of Liability */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              12. Limitation of Liability
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              To the maximum extent permitted by law:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>
                CyberActions is not liable for any security breaches, data loss, or system
                compromise
              </li>
              <li>We are not liable for indirect, incidental, or consequential damages</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">
              Liability Cap
            </h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Our total liability shall not exceed the total fees paid by you in the 12 months
              preceding the claim.
            </p>
          </div>
        </div>
      </div>

      {/* 13. Indemnification */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              13. Indemnification
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              You agree to indemnify and hold harmless CyberActions from any claims, damages, or
              liabilities arising from:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Your use of the Service</li>
              <li>Your breach of these Terms</li>
              <li>Unauthorised access to third-party systems</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 14. Termination */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              14. Termination
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We may suspend or terminate your access if:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>You breach these Terms</li>
              <li>Payment obligations are not met</li>
            </ul>
            <p className="mt-4 text-slate-600 dark:text-slate-300">Upon termination:</p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Access to the Service will cease immediately</li>
              <li>Data will be handled in accordance with our retention policy</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 15. Modifications to the Service or Terms */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              15. Modifications to the Service or Terms
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">We may:</p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Update or modify the Service</li>
              <li>Revise these Terms</li>
            </ul>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Material changes will be communicated via:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Email</li>
              <li>Platform notifications</li>
            </ul>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Continued use constitutes acceptance of the updated Terms.
            </p>
          </div>
        </div>
      </div>

      {/* 16. Intended Use and Limitations */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              16. Intended Use and Limitations
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">The Service is:</p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Not a SIEM system</li>
              <li>Not an intrusion detection system</li>
              <li>Not a managed security service</li>
            </ul>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              It is intended solely for security posture auditing and compliance monitoring.
            </p>
          </div>
        </div>
      </div>

      {/* 17. Governing Law */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              17. Governing Law
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              These Terms are governed by the laws of England and Wales.
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Any disputes shall be subject to the exclusive jurisdiction of the English courts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
